import React, { useEffect, useRef, useState } from "react";
import Sortable from "sortablejs";

import "./stylesIndividualSport.css";
import ClipPath from "../../../../../assets/icons/ic_clip-path.svg";
import Planet from "../../../../../assets/icons/ic_planet.svg";
import ToggleSwitch from "../../../../CommonComponents/ToggleSwitch/ToggleSwitch";

import { DownOutlined, PushpinFilled, PushpinOutlined, UpOutlined, TrophyOutlined } from "@ant-design/icons";

import { Select } from "antd";
import { message } from "antd";

import { API_ROUTES } from "../../../../../api/ApiRoutes";
import createPrivateAdminPanelInstance from "../../../../../api/PrivateAdminPanelInstance";
import { SPORT_LOGO_BASE_URL } from "../../../../../utils/Constants";

function IndividualSport({
  sport,
  setLiveSports,
  liveSports,
  activeTab,
  pinnedLeagues,
  setPinnedLeagues,
  reorderEntities,
  setApiIsRunning,
}) {
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.IdToken
  );

  const sportId = sport.id;
  const tableBodyRefIndividualSport = useRef(null);
  const leagueRefs = useRef({});
  const pinnedLeaguesRefs = useRef({});

  const [countries, setCountries] = useState(sport.countries);
  const [openedCountries, setOpenedCountries] = useState([]);

  // Resolve sport logo by slugifying sport name and applying overrides
  const getSportLogoUrl = (sportName) => {
    if (!sportName) return null;
    const overrides = {
      soccer: "soccer.png",
      football: "americanfootball.png",
      americanfootball: "americanfootball.png",
      basketball: "basketball.png",
      baseball: "baseball.png",
      tennis: "tennis.png",
      tabletennis: "tabletennis.png",
      icehockey: "icehockey.png",
      hockey: "icehockey.png",
      volleyball: "volleyball.png",
      handball: "handball.png",
      darts: "darts.png",
      mma: "mma.png",
      boxing: "boxing.png",
      cricket: "cricket.png",
      rugby: "rugby.png",
    };
    const normalized = String(sportName).toLowerCase().replace(/[^a-z0-9]/g, "");
    const file = overrides[normalized] || `${normalized}.png`;
    return `${SPORT_LOGO_BASE_URL}/${file}`;
  };

  const updateCountryStatus = (payload) => {
    const countryId = payload.event_code; // format: "<sport_id>#<country_id>"
    const countryStatus = payload.is_active;
    const entityId = `COUNTRY#${countryId}`;

    const previousState = [...liveSports.map((s) => ({
      ...s,
      countries: s.countries.map((c) => ({ ...c, leagues: c.leagues.map((l) => ({ ...l })) })),
    }))];

    const updated = liveSports.map((sport) => {
      if (sport.id !== sportId) return sport;

      return {
        ...sport,
        countries: sport.countries.map((c) => {
          if (c.id !== countryId) return c;

          return {
            ...c,
            status: countryStatus,
            leagues: c.leagues.map((league) => ({
              ...league,
              status: countryStatus,
            })),
          };
        }),
      };
    });

    setLiveSports(updated);

    (async () => {
      try {
        const res = await PrivateAdminPanelInstance.post(
          API_ROUTES.SET_ENTITY_ACTIVE_STATUS,
          {
            entity_type: "LIVE",
            entity_id: entityId,
            is_active: countryStatus,
          }
        );
        if (res.status !== 200 || res.data?.success === false) {
          throw new Error(`Status ${res.status}`);
        }
        message.success(
          countryStatus
            ? "Country activated successfully"
            : "Country deactivated successfully"
        );
      } catch (err) {
        console.error("Error updating country status", err);
        message.error("Error updating country status. Reverting change.");
        setLiveSports(previousState);
      }
    })();
  };

  const updateLeagueStatus = (payload) => {
    const countryId = payload.countryId;
    const leagueId = payload.league.id; // already like "LEAGUE#..."
    const nextIsActive = !payload.league.status;

    const previousState = [...liveSports.map((s) => ({
      ...s,
      countries: s.countries.map((c) => ({ ...c, leagues: c.leagues.map((l) => ({ ...l })) })),
    }))];

    const updated = liveSports.map((sport) => {
      if (sport.id !== sportId) return sport;

      return {
        ...sport,
        countries: sport.countries.map((c) =>
          c.id === countryId
            ? {
                ...c,
                leagues: c.leagues.map((l) =>
                  l.id === leagueId ? { ...l, status: nextIsActive } : l
                ),
              }
            : c
        ),
      };
    });

    setLiveSports(updated);

    (async () => {
      try {
        const res = await PrivateAdminPanelInstance.post(
          API_ROUTES.SET_ENTITY_ACTIVE_STATUS,
          {
            entity_type: "LIVE",
            entity_id: leagueId,
            is_active: nextIsActive,
          }
        );
        if (res.status !== 200 || res.data?.success === false) {
          throw new Error(`Status ${res.status}`);
        }
        message.success(
          nextIsActive
            ? "League activated successfully"
            : "League deactivated successfully"
        );
      } catch (err) {
        console.error("Error updating league status", err);
        message.error("Error updating league status. Reverting change.");
        setLiveSports(previousState);
      }
    })();
  };

  const pinLeague = async (league) => {
    try {
      setApiIsRunning(true);

      await callPinLeague("LIVE", league);
      setPinnedLeagues((prev) => {
        const updated = [
          { ...league, order: 0, sport_id: sportId },
          ...prev.map((l, index) => ({
            ...l,
            order: index + 1,
          })),
        ];

        return updated;
      });
      message.success("League pinned successfully!");
    } catch (err) {
      message.error("Error pinning league");
      console.error("Error pinning league", err);
    } finally {
      setApiIsRunning(false);
    }
  };

  const unPinLeague = async (league) => {
    try {
      setApiIsRunning(true);

      await callUnPinLeague("LIVE", league);
      setPinnedLeagues((prev) =>
        prev
          .filter((l) => l.id !== league.id)
          .map((l, index) => ({
            ...l,
            order: index,
          }))
      );
      message.success("League unpinned successfully!");
    } catch (err) {
      message.error("Error unpinning league");
      console.error("Error unpinning league", err);
    } finally {
      setApiIsRunning(false);
    }
  };

  const callPinLeague = async (entityType, league) => {
    try {
      const res = await PrivateAdminPanelInstance.post(API_ROUTES.PIN_ENTITY, {
        entity_type: entityType,
        entity_id: league.id,
      });
      if (res.status !== 200) {
        throw new Error(`Status ${res.status}`);
      }
    } catch (err) {
      console.error("Error pinning league", err);
      throw err;
    }
  };

  const callUnPinLeague = async (entityType, league) => {
    try {
      const res = await PrivateAdminPanelInstance.post(
        API_ROUTES.UNPIN_ENTITY,
        {
          entity_type: entityType,
          entity_id: league.id,
        }
      );
      if (res.status !== 200) {
        throw new Error(`Status ${res.status}`);
      }
    } catch (err) {
      console.error("Error unpinning league", err);
      throw err;
    }
  };

  const updateCountryOrder = (sportId, oldIndex, newIndex) => {
    setLiveSports((prevSports) =>
      prevSports.map((sport) => {
        if (sport.id !== sportId) return sport;

        const newCountries = [...sport.countries];

        const [movedItem] = newCountries.splice(oldIndex, 1);

        newCountries.splice(newIndex, 0, movedItem);

        const reorderedCountries = newCountries.map((item, index) => ({
          ...item,
          order: index,
        }));

        const updatedCountries = {
          ...sport,
          countries: reorderedCountries,
        };

        const previousState = prevSports;

        (async () => {
          try {
            await reorderEntities(
              "COUNTRY",
              reorderedCountries.map((country) => country.id),
              sportId
            );

            message.success("Order updated successfully!");
          } catch (err) {
            console.error("Error updating backend", err);
            setLiveSports(previousState);
            message.error("Error updating order. Please try again.");
          }
        })();

        return updatedCountries;
      })
    );
  };

  const updateLeagueOrder = (sportId, countryId, oldIndex, newIndex) => {
    setLiveSports((prevSports) =>
      prevSports.map((sport) => {
        if (sport.id !== sportId) return sport;

        return {
          ...sport,
          countries: sport.countries.map((country) => {
            if (country.id !== countryId) return country;

            const newLeagues = [...country.leagues];

            const [movedItem] = newLeagues.splice(oldIndex, 1);

            newLeagues.splice(newIndex, 0, movedItem);

            const reorderedLeagues = newLeagues.map((item, index) => ({
              ...item,
              order: index,
            }));

            console.log("country", country);
            const previousState = prevSports;

            (async () => {
              try {
                const normalizedParentId =
                  country.entity_id && country.entity_id.startsWith("COUNTRY#")
                    ? country.entity_id
                    : country.id && country.id.startsWith("COUNTRY#")
                    ? country.id
                    : country.country_id && country.country_id.startsWith("COUNTRY#")
                    ? country.country_id
                    : `COUNTRY#${sportId}#${country.country_id}`;
                await reorderEntities(
                  "LEAGUE",
                  reorderedLeagues.map((league) => league.reorder_id),
                  normalizedParentId
                );

                message.success("Order updated successfully!");
              } catch (err) {
                console.error("Error updating backend", err);
                setLiveSports(previousState);
                message.error("Error updating order. Please try again.");
              }
            })();

            return {
              ...country,
              leagues: reorderedLeagues,
            };
          }),
        };
      })
    );
  };

  const updatePinnedLeaguesOrder = (oldIndex, newIndex) => {
    setPinnedLeagues((prev) => {
      const updated = [...prev];
      const [movedItem] = updated.splice(oldIndex, 1);
      updated.splice(newIndex, 0, movedItem);

      return updated.map((l, index) => ({
        ...l,
        order: index,
      }));
    });
  };

  useEffect(() => {
    setCountries(sport.countries);
  }, [sport, liveSports]);

  useEffect(() => {
    if (!tableBodyRefIndividualSport.current) return;

    Sortable.create(tableBodyRefIndividualSport.current, {
      animation: 150,
      handle: ".drag-handle",
      onEnd: (evt) => {
        const sportId = evt.item.dataset.id;

        updateCountryOrder(sportId, evt.oldIndex, evt.newIndex);
      },
    });
  }, []);

  useEffect(() => {
    if (!leagueRefs.current) return;

    const sortables = [];
    Object.entries(leagueRefs.current).forEach(([countryId, el]) => {
      if (!el) return;

      const sortable = Sortable.create(el, {
        animation: 150,
        handle: ".drag-handle-league",
        onEnd: (evt) => {
          const sportId = evt.item.dataset.sportId;
          const countryId = evt.item.dataset.countryId;
          updateLeagueOrder(sportId, countryId, evt.oldIndex, evt.newIndex);
        },
      });

      sortables.push(sortable);
    });

    return () => {
      sortables.forEach((s) => s.destroy());
    };
  }, [openedCountries, liveSports, activeTab]);

  useEffect(() => {
    if (!pinnedLeaguesRefs.current) return;

    Sortable.create(pinnedLeaguesRefs.current, {
      animation: 150,
      handle: ".drag-handle-pinned-leagues",
      onEnd: (evt) => {
        const oldIndex = evt.oldIndex;
        const newIndex = evt.newIndex;

        updatePinnedLeaguesOrder(oldIndex, newIndex);
      },
    });
  }, []);

  return (
    <div>
      <table className="fm-individual-sport-table">
        <thead>
          <tr>
            <th style={{ width: "20%" }}># Sport Order</th>
            <th style={{ width: "20%" }}>Icon</th>
            <th style={{ width: "20%" }}>Name</th>
            <th style={{ width: "20%" }}>Order</th>
            <th style={{ width: "10%" }}>Live activity</th>
            <th style={{ width: "10%" }}></th>
          </tr>
        </thead>
      </table>

      <div className="fm-individual-sport-table-pinned-leagues-container">
        <div className="fm-individual-sport-table-pinned-leagues-header">
          <h3>Pinned leagues</h3>
        </div>
        <div
          className="fm-individual-sport-table-pinned-leagues-list drag-handle-pinned-leagues"
          ref={(el) => {
            if (el) pinnedLeaguesRefs.current = el;
          }}
        >
          {pinnedLeagues.filter((league) => league.sport_id === sportId).map((league) => (
            <div
              className="fm-individual-sport-table-pinned-leagues-list-item"
              key={league.id}
              data-id={league.id}
              data-order={league.order}
            >
              <img src={ClipPath} alt="ClipPath" />
              <div className="fm-individual-sport-table-pinned-leagues-list-item-text-container">
                <TrophyOutlined className="fm-individual-sport-table-sport-icon" />
                <img
                  className="fm-individual-sport-table-sport-icon-img"
                  src={getSportLogoUrl(sport.name)}
                  alt={`${sport.name} logo`}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
                <span className="fm-individual-sport-table-pinned-leagues-list-item-text">{league.name}</span>
              </div>
              <PushpinFilled
                style={{ color: "#FD6F27", cursor: "pointer" }}
                onClick={() => unPinLeague(league)}
              />
            </div>
          ))}
        </div>
      </div>
      <table className="fm-individual-sport-table">
        <tbody
          ref={tableBodyRefIndividualSport}
          className="fm-individual-sport-table-country-container"
        >
          {sport.countries.map((country) => (
            <React.Fragment key={country.id}>
              <tr data-id={sportId} data-order={country.order}>
                <td className="drag-handle" style={{ width: "20%" }}>
                  <div className="fm-individual-sport-table-order-container">
                    <img src={ClipPath} alt="ClipPath" />
                    {country.order + 1}
                  </div>
                </td>
                <td style={{ width: "20%" }}>
                  {country.icon ? (
                    <div className="fm-individual-sport-table-img-container">
                      <img
                        className="fm-individual-sport-table-img"
                        src={country.icon}
                        alt="Icon"
                      />
                    </div>
                  ) : (
                    <div className="fm-individual-sport-table-img-container">
                      <img
                        className="fm-individual-sport-table-img"
                        src={Planet}
                        alt="Icon"
                      />
                    </div>
                  )}
                </td>
                <td style={{ width: "20%" }}>{country.name}</td>
                <td style={{ width: "20%" }}></td>
                <td style={{ width: "10%" }}>
                  <ToggleSwitch
                    eventCode={country.id}
                    updateEventCode={updateCountryStatus}
                    status={country.status}
                    data={country}
                    onlyUpdate={false}
                  />
                </td>
                <td style={{ width: "10%" }}>
                  <div className="fm-individual-sport-table-actions-container">
                    {openedCountries.includes(country.id) ? (
                      <UpOutlined
                        onClick={() =>
                          setOpenedCountries(
                            openedCountries.filter((id) => id !== country.id)
                          )
                        }
                      />
                    ) : (
                      <DownOutlined
                        onClick={() =>
                          setOpenedCountries([...openedCountries, country.id])
                        }
                      />
                    )}
                  </div>
                </td>
              </tr>

              {openedCountries.includes(country.id) && (
                <tbody
                  ref={(el) => {
                    if (el) leagueRefs.current[country.id] = el;
                  }}
                  className="fm-individual-sport-table-league-container"
                >
                  {country.leagues.map((league) => (
                    <tr
                      key={league.id}
                      className="fm-individual-sport-table-league-row drag-handle-league children-row"
                      data-sport-id={sportId}
                      data-id={league.id}
                      data-order={league.order}
                      data-country-id={country.id}
                    >
                      <td></td>
                      <td>
                        <div className="fm-individual-sport-table-pin-container">
                          <div className="drag-handle fm-individual-sport-table-order-container">
                            <img src={ClipPath} alt="ClipPath" />
                          </div>
                          <div className="fm-individual-sport-table-sport-icon-container">
                            <TrophyOutlined className="fm-individual-sport-table-sport-icon" />
                          </div>
                          {pinnedLeagues.find((l) => l.id === league.id) ? (
                            <PushpinFilled
                              className="fm-individual-sport-table-pin-icon pinned"
                              onClick={() => unPinLeague(league)}
                            />
                          ) : (
                            <PushpinOutlined
                              className="fm-individual-sport-table-pin-icon"
                              onClick={() => pinLeague(league)}
                            />
                          )}
                        </div>
                      </td>
                      <td>
                        {league.name}
                      </td>
                      <td>
                        <Select
                          className="fm-individual-sport-table-league-order-select"
                          options={Array.from(
                            { length: country.leagues.length },
                            (_, index) => ({
                              label: `${index + 1}`,
                              value: index,
                            })
                          )}
                          onChange={(value) =>
                            updateLeagueOrder(
                              sportId,
                              country.id,
                              league.order,
                              value
                            )
                          }
                          value={league.order}
                        />
                      </td>
                      <td>
                        <ToggleSwitch
                          eventCode={league.id}
                          updateEventCode={updateLeagueStatus}
                          status={league.status}
                          data={{
                            countryId: country.id,
                            league,
                          }}
                          onlyUpdate={true}
                        />
                      </td>
                      <td></td>
                    </tr>
                  ))}
                </tbody>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default IndividualSport;

